import { useMemo, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useManagerStandings } from '@/hooks/useManagerStandings'
import { useTeams } from '@/hooks/useTeams'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { supabase } from '@/integrations/supabase/client'
import { getApiSeasonYear } from '@/lib/season'
import { useSeason } from '@/context/SeasonContext'
import { getInformalName, getTeamLogo } from '@/lib/teams'
import { useSyncFixturesWithOdds } from '@/hooks/useSyncFixturesWithOdds'
import { ClubSummary } from './components'
import type { EplTableEntry, ManagerClubStat, ManagerOption, FormResult } from './types'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function CompetitionSection() {
  const { user, currentMembership } = useAuth()
  const { season } = useSeason()
  const { competitionId, competition } = useLeagueConfig()
  const apiYear = getApiSeasonYear(season)
  const leagueId = currentMembership?.league_id

  // Fetch manager standings for the manager list
  const { data: standings = [], isLoading: isLoadingStandings } = useManagerStandings(season)

  // Fetch competition teams for logos and dynamic team list
  const { data: teamsData, isLoading: isLoadingTeams } = useTeams(apiYear)
  const teams = teamsData?.teams

  // Fixture sync for empty leagues
  const queryClient = useQueryClient()
  const { syncAllFixtures, isLoading: isSyncingFixtures } = useSyncFixturesWithOdds()
  const hasSyncedRef = useRef(false)

  // Derive dynamic team names from API data (works for any competition)
  const teamNames = useMemo(() => {
    if (!teams?.length) return []
    return teams.map((t) => ({
      informal: getInformalName(t.name),
    }))
  }, [teams])

  // Fetch fixtures for this competition's table
  const { data: fixtures = [], isLoading: isLoadingFixtures } = useQuery({
    queryKey: ['all-fixtures', season, competitionId],
    enabled: competitionId != null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fixtures')
        .select('matchday, home_team, away_team, home_score, away_score, status, kickoff_time')
        .eq('season', season)
        .eq('competition_id', competitionId!)
        .order('matchday', { ascending: false })
        .order('kickoff_time', { ascending: false })
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60 * 1000,
  })

  // Fetch all league selections for manager stats
  const { data: allSelections = [], isLoading: isLoadingSelections } = useQuery({
    queryKey: ['league-selections', leagueId, season],
    queryFn: async () => {
      if (!leagueId) return []
      const { data, error } = await supabase
        .from('user_selections')
        .select('user_id, team_name, points_earned, processed')
        .eq('league_id', leagueId)
        .eq('season', season)
      if (error) throw error
      return data ?? []
    },
    enabled: !!leagueId,
    staleTime: 2 * 60 * 1000,
  })

  // Build EPL table from fixtures
  const eplTable: EplTableEntry[] = useMemo(() => {
    // Initialize stats for all 20 teams
    const teamStats = new Map<string, {
      played: number
      wins: number
      draws: number
      losses: number
      goalsFor: number
      goalsAgainst: number
      points: number
      form: FormResult[]
    }>()

    teamNames.forEach((team) => {
      teamStats.set(team.informal, {
        played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        points: 0,
        form: [],
      })
    })

    // Get unique fixtures (dedupe by matchday+teams, keep latest)
    const seenFixtures = new Set<string>()
    const completedFixtures = fixtures
      .filter(f => f.status === 'FT')
      .filter(f => {
        const key = `${f.matchday}:${f.home_team}:${f.away_team}`
        if (seenFixtures.has(key)) return false
        seenFixtures.add(key)
        return true
      })

    // Sort fixtures by matchday ascending for correct form order
    const sortedFixtures = [...completedFixtures].sort((a, b) => a.matchday - b.matchday)

    // Process each completed fixture
    sortedFixtures.forEach((fixture) => {
      const homeTeam = getInformalName(fixture.home_team)
      const awayTeam = getInformalName(fixture.away_team)
      const homeScore = fixture.home_score ?? 0
      const awayScore = fixture.away_score ?? 0

      const homeStats = teamStats.get(homeTeam)
      const awayStats = teamStats.get(awayTeam)

      if (homeStats) {
        homeStats.played++
        homeStats.goalsFor += homeScore
        homeStats.goalsAgainst += awayScore

        if (homeScore > awayScore) {
          homeStats.wins++
          homeStats.points += 3
          homeStats.form.push('W')
        } else if (homeScore < awayScore) {
          homeStats.losses++
          homeStats.form.push('L')
        } else {
          homeStats.draws++
          homeStats.points += 1
          homeStats.form.push('D')
        }
      }

      if (awayStats) {
        awayStats.played++
        awayStats.goalsFor += awayScore
        awayStats.goalsAgainst += homeScore

        if (awayScore > homeScore) {
          awayStats.wins++
          awayStats.points += 3
          awayStats.form.push('W')
        } else if (awayScore < homeScore) {
          awayStats.losses++
          awayStats.form.push('L')
        } else {
          awayStats.draws++
          awayStats.points += 1
          awayStats.form.push('D')
        }
      }
    })

    // Find next fixture for each team
    const nextOpponents = new Map<string, { teamId: string; shortName: string; logo: string }>()
    const scheduledFixtures = fixtures
      .filter(f => f.status === 'NS' || f.status === 'TBD')
      .sort((a, b) => new Date(a.kickoff_time).getTime() - new Date(b.kickoff_time).getTime())

    const teamsWithNextFixture = new Set<string>()
    scheduledFixtures.forEach((fixture) => {
      const homeTeam = getInformalName(fixture.home_team)
      const awayTeam = getInformalName(fixture.away_team)

      if (!teamsWithNextFixture.has(homeTeam)) {
        teamsWithNextFixture.add(homeTeam)
        nextOpponents.set(homeTeam, {
          teamId: awayTeam.toLowerCase().replace(/\s+/g, '-'),
          shortName: awayTeam,
          logo: getTeamLogo(awayTeam, teams),
        })
      }

      if (!teamsWithNextFixture.has(awayTeam)) {
        teamsWithNextFixture.add(awayTeam)
        nextOpponents.set(awayTeam, {
          teamId: homeTeam.toLowerCase().replace(/\s+/g, '-'),
          shortName: homeTeam,
          logo: getTeamLogo(homeTeam, teams),
        })
      }
    })

    // Convert to array and sort by points, then GD, then goals scored
    const entries: EplTableEntry[] = Array.from(teamStats.entries()).map(([teamName, stats]) => ({
      rank: 0,
      teamId: teamName.toLowerCase().replace(/\s+/g, '-'),
      shortName: teamName,
      logo: getTeamLogo(teamName, teams),
      played: stats.played,
      wins: stats.wins,
      draws: stats.draws,
      losses: stats.losses,
      goalsFor: stats.goalsFor,
      goalsAgainst: stats.goalsAgainst,
      goalDifference: stats.goalsFor - stats.goalsAgainst,
      points: stats.points,
      form: stats.form.slice(-5) as FormResult[],
      nextOpponent: nextOpponents.get(teamName) ?? null,
    }))

    // Sort by points (desc), goal difference (desc), goals scored (desc), name (asc)
    entries.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points
      if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference
      if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor
      return a.shortName.localeCompare(b.shortName)
    })

    // Assign ranks
    entries.forEach((entry, index) => {
      entry.rank = index + 1
    })

    return entries
  }, [fixtures, teams, teamNames])

  // Build manager stats from selections
  const managerStats: Record<string, ManagerClubStat[]> = useMemo(() => {
    const stats: Record<string, ManagerClubStat[]> = {}

    // Helper to create empty stats for all teams
    const createEmptyStats = () => teamNames.map((team) => ({
      teamId: team.informal.toLowerCase().replace(/\s+/g, '-'),
      timesPicked: 0,
      points: 0,
    }))

    // Initialize stats for all managers in standings (even those with no selections)
    standings.forEach((s) => {
      stats[s.user_id] = createEmptyStats()
    })

    // Group selections by user_id
    const selectionsByUser = new Map<string, typeof allSelections>()
    allSelections.forEach((sel) => {
      const existing = selectionsByUser.get(sel.user_id) ?? []
      existing.push(sel)
      selectionsByUser.set(sel.user_id, existing)
    })

    // For each user with selections, compute per-team stats
    selectionsByUser.forEach((selections, userId) => {
      const teamMap = new Map<string, { timesPicked: number; points: number }>()

      // Initialize all teams with 0s
      teamNames.forEach((team) => {
        teamMap.set(team.informal.toLowerCase().replace(/\s+/g, '-'), { timesPicked: 0, points: 0 })
      })

      // Accumulate picks and points
      selections.forEach((sel) => {
        const teamName = getInformalName(sel.team_name)
        const teamId = teamName.toLowerCase().replace(/\s+/g, '-')
        const existing = teamMap.get(teamId) ?? { timesPicked: 0, points: 0 }
        existing.timesPicked++
        if (sel.processed) {
          existing.points += sel.points_earned ?? 0
        }
        teamMap.set(teamId, existing)
      })

      stats[userId] = Array.from(teamMap.entries()).map(([teamId, data]) => ({
        teamId,
        timesPicked: data.timesPicked,
        points: data.points,
      }))
    })

    // Add autopick stats (placeholder - all zeros for now)
    stats['autopick'] = createEmptyStats()

    return stats
  }, [allSelections, standings, teamNames])

  // Build manager list from standings
  const managers: ManagerOption[] = useMemo(() => {
    return standings.map((s) => ({
      id: s.user_id,
      displayName: s.user_name,
      initials: getInitials(s.user_name),
    }))
  }, [standings])

  // Auto-sync fixtures when a new league has none
  const isDataLoaded = competitionId != null && !isLoadingFixtures && !isLoadingTeams
  useEffect(() => {
    if (isDataLoaded && fixtures.length === 0 && !isSyncingFixtures && !hasSyncedRef.current) {
      hasSyncedRef.current = true
      syncAllFixtures(season).then(() => {
        queryClient.invalidateQueries({ queryKey: ['all-fixtures', season, competitionId] })
      }).catch((err) => {
        console.error('Auto-sync failed:', err)
        // Don't reset hasSyncedRef — user can manually retry via button
      })
    }
  }, [isDataLoaded, fixtures.length, isSyncingFixtures, season, syncAllFixtures, queryClient])

  // Reset sync ref when switching leagues/seasons
  useEffect(() => {
    hasSyncedRef.current = false
  }, [leagueId, season])

  // Loading state
  if (!user) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <p>Please log in to view standings.</p>
      </div>
    )
  }

  if (isLoadingStandings || isLoadingTeams || isLoadingFixtures || isLoadingSelections) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  // Empty state: no fixtures synced yet for this competition
  if (fixtures.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400 mx-auto" />
        <p className="text-zinc-500 dark:text-zinc-400">
          Syncing {competition.name} fixtures...
        </p>
        <p className="text-sm text-zinc-400 dark:text-zinc-500">
          This may take a moment for the first sync.
        </p>
        {!isSyncingFixtures && (
          <button
            onClick={() => {
              hasSyncedRef.current = false
              syncAllFixtures(season).then(() => {
                queryClient.invalidateQueries({ queryKey: ['all-fixtures', season, competitionId] })
              })
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm font-medium
                       hover:bg-zinc-800
                       dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
          >
            Retry Sync
          </button>
        )}
      </div>
    )
  }

  return (
    <ClubSummary
      eplTable={eplTable}
      managerStats={managerStats}
      managers={managers}
      currentUserId={user.id}
      competitionCode={competition.code}
    />
  )
}
